/**
 * "Page load" / "Load time over the period" chart — P95 and average per
 * slot (hour or UTC day), page loads as faint bars, the warning and critical
 * limits as dashed lines, and backend restarts as dashed verticals.
 *
 * Built on the dashboards' chart conventions (TrendChart in Charts.jsx):
 * it measures its own width and draws in real pixels, uses the shared
 * `useChartHover` + `ChartTooltip`, and takes every colour from tokens.
 * Every axis label and tooltip figure carries its unit.
 */
import { useLayoutEffect, useState } from 'react'
import { useChartHover } from '../charts/use-chart-hover'
import { ChartTooltip } from '../charts/ChartTooltip'
import { niceTicks } from '../charts/scale'
import { formatTime } from '../../lib/format'
import {
  countOf,
  formatLimitSeconds,
  formatMs,
  slotLabel,
  slotLongLabel,
} from '../../lib/page-perf/view-model'
import { CHART } from './perf-tones'

const HEIGHT = 240
const PAD_L = 52
const PAD_R = 10
const PAD_T = 18
const PAD_B = 26

const isNum = (v) => typeof v === 'number' && Number.isFinite(v)

function LegendItem({ color, label, dashed, bar }) {
  return (
    <span className="inline-flex items-center gap-1.5">
      {bar ? (
        <span aria-hidden="true" className="inline-block h-2.5 w-2.5 rounded-sm" style={{ backgroundColor: color, opacity: 0.25 }} />
      ) : (
        <svg width="18" height="8" aria-hidden="true">
          <line x1="0" x2="18" y1="4" y2="4" stroke={color} strokeWidth="2" strokeDasharray={dashed ? '4 3' : undefined} />
        </svg>
      )}
      {label}
    </span>
  )
}

/**
 * The empty state and the drawn chart are separate components on purpose:
 * the chart's size observer lives with the element it measures, so when a
 * live tick turns "no opens" into data the chart mounts with its own
 * observer instead of inheriting one attached to the empty box.
 */
export function LoadTimeChart({ slots = [], restarts = [], slot = 'hour', limits, emptyText }) {
  const anyOpens = slots.some((s) => (s.loads ?? 0) > 0 || isNum(s.p95Ms))
  if (slots.length === 0 || !anyOpens) {
    return (
      <div className="flex items-center justify-center rounded-lg border border-dashed border-[color:var(--color-rule-strong)]" style={{ height: HEIGHT }}>
        <p className="px-4 text-center text-sm italic text-[color:var(--color-ink-muted)]">{emptyText}</p>
      </div>
    )
  }
  return <ChartBody slots={slots} restarts={restarts} slot={slot} limits={limits} />
}

function ChartBody({ slots, restarts, slot, limits }) {
  const { ref, hover, track, clear } = useChartHover()
  const [width, setWidth] = useState(0)

  useLayoutEffect(() => {
    const el = ref.current
    if (!el || typeof ResizeObserver === 'undefined') return undefined
    // The observer reports the first size itself (after layout, before
    // paint), so the chart draws at its real width on its first frame.
    const ro = new ResizeObserver(() => setWidth(el.clientWidth))
    ro.observe(el)
    return () => ro.disconnect()
  }, [ref])

  const n = slots.length
  const warn = limits?.load?.warning
  const crit = limits?.load?.critical
  const dataMax = Math.max(1, ...slots.flatMap((s) => [s.p95Ms, s.avgMs].filter(isNum)))
  const ticks = niceTicks(Math.max(dataMax, isNum(warn) ? warn : 0) * 1.08, { integer: true })
  const yMax = ticks[ticks.length - 1] || 1
  const maxLoads = Math.max(1, ...slots.map((s) => s.loads ?? 0))

  const w = Math.max(width, 2)
  const plotW = Math.max(1, w - PAD_L - PAD_R)
  const plotH = HEIGHT - PAD_T - PAD_B
  const band = plotW / n
  const xAt = (i) => PAD_L + (i + 0.5) * band
  const yAt = (v) => PAD_T + plotH - (v / yMax) * plotH
  const baseY = PAD_T + plotH

  // A line that breaks where a slot had no opens (no invented values).
  const linePath = (key) => {
    let d = ''
    let pen = false
    slots.forEach((s, i) => {
      const v = s[key]
      if (!isNum(v)) {
        pen = false
        return
      }
      d += `${pen ? 'L' : 'M'}${xAt(i).toFixed(1)},${yAt(v).toFixed(1)} `
      pen = true
    })
    return d.trim()
  }

  // Restarts are placed by time inside their slot.
  const slotMs = slot === 'day' ? 86_400_000 : 3_600_000
  const starts = slots.map((s) => new Date(s.t).getTime())
  const restartMarks = (restarts ?? [])
    .map((iso) => {
      const r = new Date(iso).getTime()
      if (!Number.isFinite(r)) return null
      const i = starts.findIndex((t) => Number.isFinite(t) && r >= t && r < t + slotMs)
      if (i < 0) return null
      return { iso, i, x: PAD_L + (i + (r - starts[i]) / slotMs) * band }
    })
    .filter(Boolean)
  // Label a restart only when it is far enough from the last labelled one.
  const labelledRestarts = []
  for (const m of restartMarks) {
    const last = labelledRestarts.findLast((x) => x.show)
    labelledRestarts.push({ ...m, show: !last || m.x - last.x > 90 })
  }

  const maxLabels = Math.max(2, Math.floor(plotW / 72))
  const labelStep = Math.max(1, Math.ceil(n / maxLabels))
  const critOnChart = isNum(crit) && crit <= yMax
  const warnOnChart = isNum(warn) && warn <= yMax
  const hoverIdx = hover?._i

  return (
    <div>
      <div ref={ref} className="relative w-full" style={{ height: HEIGHT }}>
        {width > 0 ? (
          <svg
            width={w}
            height={HEIGHT}
            role="img"
            aria-label={`Line chart of the P95 and average load time per ${slot === 'day' ? 'day' : 'hour'}${isNum(warn) ? `, with the warning limit at ${formatLimitSeconds(warn)}` : ''}${isNum(crit) ? ` and the critical limit at ${formatLimitSeconds(crit)}` : ''}${restartMarks.length ? `, and ${countOf(restartMarks.length, 'backend restart')} marked` : ''}`}
          >
            {ticks.map((v) => (
              <g key={`t${v}`}>
                <line x1={PAD_L} x2={w - PAD_R} y1={yAt(v)} y2={yAt(v)} stroke={CHART.grid} strokeWidth="1" opacity={v === 0 ? 0.9 : 0.45} />
                <text x={PAD_L - 6} y={yAt(v) + 3} textAnchor="end" fill={CHART.axis} style={{ fontSize: 10 }}>
                  {formatMs(v)}
                </text>
              </g>
            ))}
            {/* Page loads as faint bars, on their own scale (up to a third of the height). */}
            {slots.map((s, i) =>
              (s.loads ?? 0) > 0 ? (
                <rect
                  key={`b${i}`}
                  x={xAt(i) - Math.min(14, band * 0.35)}
                  width={Math.min(28, band * 0.7)}
                  y={baseY - ((s.loads ?? 0) / maxLoads) * plotH * 0.33}
                  height={((s.loads ?? 0) / maxLoads) * plotH * 0.33}
                  fill={CHART.loads}
                  opacity="0.16"
                />
              ) : null,
            )}
            {warnOnChart ? (
              <g>
                <line x1={PAD_L} x2={w - PAD_R} y1={yAt(warn)} y2={yAt(warn)} stroke={CHART.warning} strokeDasharray="5 4" strokeWidth="1.25" />
                <text x={w - PAD_R - 2} y={yAt(warn) - 4} textAnchor="end" fill={CHART.axis} style={{ fontSize: 10 }}>
                  warning {formatLimitSeconds(warn)}
                </text>
              </g>
            ) : null}
            {critOnChart ? (
              <g>
                <line x1={PAD_L} x2={w - PAD_R} y1={yAt(crit)} y2={yAt(crit)} stroke={CHART.critical} strokeDasharray="5 4" strokeWidth="1.25" />
                <text x={w - PAD_R - 2} y={yAt(crit) - 4} textAnchor="end" fill={CHART.axis} style={{ fontSize: 10 }}>
                  critical {formatLimitSeconds(crit)}
                </text>
              </g>
            ) : null}
            {labelledRestarts.map((m) => (
              <g key={`r${m.iso}`}>
                <line x1={m.x} x2={m.x} y1={PAD_T - 6} y2={baseY} stroke={CHART.restart} strokeDasharray="3 3" strokeWidth="1" />
                {m.show ? (
                  <text
                    x={m.x > w - 110 ? m.x - 3 : m.x + 3}
                    y={PAD_T - 8}
                    textAnchor={m.x > w - 110 ? 'end' : 'start'}
                    fill={CHART.axis}
                    style={{ fontSize: 9 }}
                  >
                    restart {formatTime(m.iso)}
                  </text>
                ) : null}
              </g>
            ))}
            <path d={linePath('avgMs')} fill="none" stroke={CHART.avg} strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round" />
            <path d={linePath('p95Ms')} fill="none" stroke={CHART.p95} strokeWidth="2.25" strokeLinecap="round" strokeLinejoin="round" />
            {/* Points too, so a slot with no neighbour still shows both figures. */}
            {slots.map((s, i) =>
              isNum(s.avgMs) ? <circle key={`a${i}`} cx={xAt(i)} cy={yAt(s.avgMs)} r={hoverIdx === i ? 3.5 : 2} fill={CHART.avg} /> : null,
            )}
            {slots.map((s, i) =>
              isNum(s.p95Ms) ? <circle key={`p${i}`} cx={xAt(i)} cy={yAt(s.p95Ms)} r={hoverIdx === i ? 4 : 2.5} fill={CHART.p95} /> : null,
            )}
            {hoverIdx != null ? (
              <line x1={xAt(hoverIdx)} x2={xAt(hoverIdx)} y1={PAD_T} y2={baseY} stroke="var(--color-rule-strong)" strokeWidth="1" />
            ) : null}
            {slots.map((s, i) =>
              i % labelStep === 0 ? (
                <text
                  key={`x${i}`}
                  x={xAt(i)}
                  y={HEIGHT - 8}
                  textAnchor="middle"
                  fill={CHART.axis}
                  style={{ fontSize: 10 }}
                >
                  {slotLabel(s.t, slot)}
                </text>
              ) : null,
            )}
            {slots.map((s, i) => {
              const inSlot = restartMarks.filter((m) => m.i === i)
              const hint = [
                `Average ${formatMs(s.avgMs)}`,
                countOf(s.loads ?? 0, 'open'),
                isNum(warn) ? `warning over ${formatLimitSeconds(warn)}` : null,
                isNum(crit) ? `critical over ${formatLimitSeconds(crit)}` : null,
                inSlot.length ? `backend restart ${inSlot.map((m) => formatTime(m.iso)).join(', ')}` : null,
              ]
                .filter(Boolean)
                .join(' · ')
              const payload = {
                _i: i,
                color: CHART.p95,
                label: slotLongLabel(s.t, slot),
                value: isNum(s.p95Ms) ? `P95 ${formatMs(s.p95Ms)}` : 'no opens',
                hint,
              }
              return (
                <rect
                  key={`h${i}`}
                  x={PAD_L + i * band}
                  y={PAD_T}
                  width={band}
                  height={plotH}
                  fill="transparent"
                  onMouseEnter={track(payload)}
                  onMouseMove={track(payload)}
                  onMouseLeave={clear}
                />
              )
            })}
          </svg>
        ) : null}
        {hover ? <ChartTooltip {...hover} /> : null}
      </div>
      <div className="mt-2 flex flex-wrap items-center gap-x-4 gap-y-1 text-xs text-[color:var(--color-ink-muted)]">
        <LegendItem color={CHART.p95} label="P95 load time" />
        <LegendItem color={CHART.avg} label="Average load time" />
        <LegendItem color={CHART.loads} label="Page loads" bar />
        {isNum(warn) ? <LegendItem color={CHART.warning} label={`Warning over ${formatLimitSeconds(warn)}`} dashed /> : null}
        {isNum(crit) ? <LegendItem color={CHART.critical} label={`Critical over ${formatLimitSeconds(crit)}`} dashed /> : null}
        <LegendItem color={CHART.restart} label={`Backend restart${restartMarks.length ? ` (${restartMarks.length})` : ' — none in this period'}`} dashed />
        {isNum(crit) && !critOnChart ? (
          <span>The critical limit ({formatLimitSeconds(crit)}) is above the top of the chart.</span>
        ) : null}
        {!limits?.load ? <span>The server did not send the load-time limits.</span> : null}
      </div>
    </div>
  )
}
